<template>
    <QLayout view="lHh LpR fFf" class="Dashboard">
        <Sidebar
            v-model="tab"
            :tabs="tabs"
            @update:modelValue="handleChangeTab"
        />

        <QDrawer
            v-if="hasContextDrawer"
            v-model="rightDrawerOpen"
            side="right"
            :overlay="isMobile"
            :width="320"
            class="ContextDrawer q-px-sm"
        >
            <div class="ContextDrawerHeader">
                <div class="flex items-center no-wrap">
                    <q-icon
                        :name="drawerIcon"
                        color="primary"
                        size="20px"
                        class="q-mr-sm"
                    />
                    <div class="ContextDrawerTitle text-primary">
                        {{ drawerTitle }}
                    </div>
                </div>
            </div>
            <q-separator color="primary" />
            <div class="q-pa-md">
                <component
                    :is="contextDrawerPanel.component"
                    v-bind="contextDrawerPanel.props"
                    v-on="contextDrawerPanel.on"
                />
            </div>
        </QDrawer>
        <QHeader
            v-if="meta.header"
            elevated
            class="Header"
        >
            <AdminHeader>
                <template #actions>
                    <div
                        v-if="hasContextDrawer"
                        class="flex row-stretch-center"
                    >
                        <QBtn
                            flat
                            round
                            dense
                            color="primary"
                            icon="view_sidebar"
                            :aria-label="$t('general.filters')"
                            @click="rightDrawerOpen = !rightDrawerOpen"
                        />
                    </div>
                    <HeaderList
                        v-else
                        v-model="filters"
                        :search="meta.search"
                    />
                </template>
            </AdminHeader>
        </QHeader>
        <QPageContainer>
            <QPage
                class="Screen flex-1"
                :class="{ isDesktop: !isMobile }"
            >
                <RouterView v-slot="{ Component }">
                    <Transition
                        name="main-router"
                        mode="out-in"
                    >
                        <component
                            :is="Component"
                            ref="activeView"
                            :filters="filters"
                        />
                    </Transition>
                </RouterView>
            </QPage>
        </QPageContainer>
    </QLayout>
</template>

<script>

import { useI18n } from 'vue-i18n'
import { AdminHeader, HeaderList, Sidebar } from '@/components'
import { mediaQueryMixin } from '@/mixins'
import {
    DashboardOrganizationsDrawerPanel,
    dashboardContextDrawerRoutes,
    getDashboardContextDrawerPanel,
} from '@/screens/Dashboard/contextDrawerPanels'

export default {
    name: 'DashboardLayout',
    components: {
        AdminHeader,
        HeaderList,
        Sidebar,
        DashboardOrganizationsDrawerPanel,
    },
    mixins: [mediaQueryMixin],
    setup() {
        const { t } = useI18n() // use as global scope
        return { t }
    },
    data() {
        return {
            tab: '',
            rightDrawerOpen: true,
            drawerSearch: '',
            orgSortBy: 'NAME',
            orgSortDirection: 'ASCENDING',
            filters: {
                search: '',
                archived: false,
            },
        }
    },
    computed: {
        routeName() {
            return this.$route?.name
        },
        meta() {
            return this.$route?.meta
        },
        hasContextDrawer() {
            return dashboardContextDrawerRoutes.includes(this.routeName)
        },
        contextDrawerPanel() {
            return getDashboardContextDrawerPanel(this)
        },
        drawerTitle() {
            return this.t('navigation.organizations')
        },
        drawerIcon() {
            return this.tabs?.[0]?.icon || 'tune'
        },
        orgSortOptions() {
            return [
                { label: this.$t('general.name'), value: 'NAME' },
                { label: this.$t('general.createdAt'), value: 'CREATED_AT' },
            ]
        },
        sortDirectionOptions() {
            return [
                { label: this.$t('general.ascending'), value: 'ASCENDING' },
                { label: this.$t('general.descending'), value: 'DESCENDING' },
            ]
        },
        tabs() {
            return [
                {
                    id: 'organizations',
                    icon: 'group_work',
                    label: this.t('navigation.organizations'),
                    screen: 'Organizations',
                },
            ]
        },
    },
    watch: {
        '$route.name': {
            handler() {
                this.tab = this.getTabName()
                if (!this.hasContextDrawer) return
                this.syncFromStorage()
                this.drawerSearch = this.filters.search || ''
            },
            immediate: true,
        },
    },
    mounted() {
        this.tab = this.getTabName()

        if (this.isMobile) this.rightDrawerOpen = false
    },
    updated() {
        this.tab = this.getTabName()
    },
    methods: {
        syncFromStorage() {
            try {
                const sortBy = localStorage.getItem('organizationsListSortBy')
                if (sortBy) this.orgSortBy = sortBy
            } catch (e) { /* ignore */ }

            try {
                const sortDirection = localStorage.getItem('organizationsListSortDirection')
                if (sortDirection) this.orgSortDirection = sortDirection
            } catch (e) { /* ignore */ }
        },
        handleCreate() {
            this.$refs.activeView?.handleNewItem?.()
        },
        applySearch() {
            this.filters = { ...this.filters, search: this.drawerSearch }
        },
        applyOrgSort() {
            try { localStorage.setItem('organizationsListSortBy', this.orgSortBy) } catch (e) { /* ignore */ }
            try { localStorage.setItem('organizationsListSortDirection', this.orgSortDirection) } catch (e) { /* ignore */ }

            this.$refs.activeView?.fetchData?.()
        },
        handleChangeTab(value) {
            const tab = this.tabs.find(({ id }) => id === value)
            if (tab && !tab.disabled) this.$router.push({ name: tab.screen }).catch(() => {})
        },
        getTabName() {
            const { name } = this.$route
            return name.toLowerCase()
        },
    },
}
</script>

<style lang="scss">

.ContextDrawer {
    background: $gray-bg !important;

    .q-field--outlined .q-field__control {
        background: $gray-bg;
    }

    .q-field--outlined:not(.q-field--focused) .q-field__control:before {
        border-color: $gray-04;
        background: $gray-controls;
    }

    .q-btn {
        font-weight: 500;
    }

    .q-btn--outline:before,
    .q-btn-group--outline:before {
        border-width: 2px;
    }
}

.ContextDrawerHeader {
    padding: 0 16px;
    height: $header-height;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ContextDrawerTitle {
    font-weight: 500;
    letter-spacing: 1.25px;
}
</style>
<style scoped lang="scss">
.Header {
    background: $white;
    height: $header-height;
}

.Screen {
    padding: 25px;
    height: 100%;
    overflow: auto;
    width: 100%;

    background: $gray-bg;
}
</style>
